import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  HostBinding,
  inject,
  input,
  NO_ERRORS_SCHEMA,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { User } from '../../common/models/user';
import { AssistantService } from '../../common/services/assistant.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-vatutor-chat',
  standalone: true,
  imports: [],
  templateUrl: './vatutor-chat.component.html',
  styleUrl: './vatutor-chat.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class VatutorChatComponent implements OnInit {
  authService = inject(AuthService);
  assistantService = inject(AssistantService);
  env = input<string | null>(null);
  token = input<string | null>(null);
  intro = input<string>('');
  standalone = input<boolean>(false);
  widget = viewChild<any>('chat');
  courseId = input<string | null>(null);
  subTenantName = input<string | null>(null);
  userType = input<string>('default');
  useGenerateEndpoint = input<boolean>(false);
  saveAs = input<string | null>(null);
  chatClosed = input<boolean>(true);
  dialog = input<boolean>(false);
  userInfo = signal<User | null>(null);
  sessionId = signal<string | null>(null);
  chatReady = signal(false);

  private readonly chatConfig = (environment as any).chatConfig;
  readonly chatEnv: string = (environment as any).envName === 'prod' ? 'prod' : 'uat';
  readonly resolvedEnv = computed(() => this.env() ?? this.chatEnv);
  private chatInitialized = false;

  @HostBinding('class.standalone-mode') get isStandalone() {
    return this.standalone() === true;
  }

  constructor() {
    this.authService.userInfo$.subscribe(user => {
      this.userInfo.set(user);
    });

    effect(() => {
      const ready = this.chatReady();
      const widgetRef = this.widget();
      if (ready && widgetRef && !this.chatInitialized) {
        this.chatInitialized = true;
        this.onChatWidgetReady(widgetRef.nativeElement);
      }
    });
  }

  ngOnInit(): void {
    this.loadChatBundle();
  }

  private loadChatBundle(): void {
    if (this.chatConfig?.useRemoteLoader) {
      const url = this.chatEnv === 'prod'
        ? this.chatConfig.remoteProdUrl
        : this.chatConfig.remoteTestUrl;

      if (url) {
        console.log(`[VaTutor] Loading chat from CloudFront (${this.chatEnv}):`, url);
        this.loadRemoteScript(url);
        return;
      }
    }

    console.log('[VaTutor] Loading local chat bundle');
    // @ts-ignore
    import('./vatutor-chat.bundle.js').then(() => {
      this.chatReady.set(true);
    }).catch((err: unknown) => {
      console.error('[VaTutor] Error loading local chat bundle:', err);
    });
  }

  private loadRemoteScript(url: string): void {
    const scriptId = 'vatutor-chat-script-remote';
    const existing = document.getElementById(scriptId);

    if (existing) {
      this.chatReady.set(true);
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = url;
    script.type = 'module';
    script.async = true;
    script.onload = () => {
      console.log('[VaTutor] Remote chat bundle loaded successfully');
      this.chatReady.set(true);
    };
    script.onerror = (err) => {
      console.error('[VaTutor] Failed to load remote chat bundle:', url, err);
      console.log('[VaTutor] Falling back to local bundle');
      // @ts-ignore
      import('./vatutor-chat.bundle.js').then(() => {
        this.chatReady.set(true);
      }).catch((localErr: unknown) => {
        console.error('[VaTutor] Local fallback also failed:', localErr);
      });
    };

    document.head.appendChild(script);
  }

  private onChatWidgetReady(widgetElement: any): void {
    const shouldUseGenerate = this.useGenerateEndpoint();

    if (shouldUseGenerate && widgetElement) {
      setTimeout(async () => {
        this.setupGenerateHandler(widgetElement);

        widgetElement.closed = false;

        await widgetElement.updateComplete;

        const bwcChat = widgetElement._bwcChat;

        if (bwcChat) {
          bwcChat.showSpinner = true;

          try {
            const messages = await widgetElement._chatHandler.getMessages();

            for (const message of messages) {
              bwcChat.insertMessage(message);
            }

            bwcChat._initialized = true;
            bwcChat.showSpinner = false;

            bwcChat.requestUpdate();

          } catch (error) {
            console.error('Error initializing chat:', error);
            bwcChat.showSpinner = false;
            bwcChat.insertMessage({
              content: 'Si è verificato un errore durante l\'inizializzazione.',
              sent: false,
              date: new Date().toISOString(),
              attachments: []
            });
          }
        }
      }, 150);
    }
  }

  private setupGenerateHandler(widgetElement: any): void {
    const self = this;

    widgetElement._chatHandler = {
      getMessages: async () => {
        if (!self.hasCourseSelected()) {
          return [self.noCourseSelectedMessage()];
        }

        const user = self.userInfo();
        const message = `Name: ${user?.name || 'User'}`;

        const payload: any = {
          message: message,
          language: 'it',
          userType: self.userType() || 'user',
          course: self.courseId(),
          saveAs: self.saveAs() || '',
          ...(self.subTenantName() ? { subTenantName: self.subTenantName() } : {})
        };

        try {
          const response: any = await self.assistantService.generate(payload).toPromise();

          self.sessionId.set(response?.sessionId || null);
          widgetElement._sessionId = response?.sessionId;

          return [{
            content: self.sanitizeMessage(response?.message || ''),
            sent: false,
            date: new Date().toISOString(),
            attachments: self.getAttachmentsFromMessage(response)
          }];
        } catch (error) {
          console.error('Error calling generate endpoint:', error);
          return [{
            content: 'Si è verificato un errore durante la generazione.',
            sent: false,
            date: new Date().toISOString(),
            attachments: []
          }];
        }
      },
      sendMessage: async (message: string) => {
        if (!self.hasCourseSelected()) {
          return self.noCourseSelectedMessage();
        }

        const normalizedMessage = self.normalizeMessageContent(message);

        const payload: any = {
          message: normalizedMessage,
          sessionId: self.sessionId(),
          language: 'it',
          userType: self.userType() || 'user',
          course: self.courseId(),
          saveAs: self.saveAs() || '',
          ...(self.subTenantName() ? { subTenantName: self.subTenantName() } : {})
        };

        try {
          const response: any = await self.assistantService.generate(payload).toPromise();

          if (!self.sessionId()) {
            self.sessionId.set(response?.sessionId || null);
            widgetElement._sessionId = response?.sessionId;
          }

          return {
            content: self.sanitizeMessage(response?.message || ''),
            sent: false,
            date: new Date().toISOString(),
            attachments: self.getAttachmentsFromMessage(response)
          };
        } catch (error) {
          console.error('Error calling generate endpoint:', error);
          return {
            content: 'Si è verificato un errore durante la generazione.',
            sent: false,
            date: new Date().toISOString(),
            attachments: []
          };
        }
      }
    };
  }

  private hasCourseSelected(): boolean {
    return !!this.courseId();
  }

  private noCourseSelectedMessage(): any {
    return {
      content: 'Nessun insegnamento selezionato. Seleziona un insegnamento per usare la chat.',
      sent: false,
      date: new Date().toISOString(),
      attachments: []
    };
  }

  private normalizeMessageContent(message: string): string {
    return message
      .replace(/&lt;br\s*\/?&gt;/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/\\n/g, '\n');
  }

  private sanitizeMessage(message: string): string {
    const sanitized = this.normalizeMessageContent(message);
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitized, 'text/html');
    
    return doc.body.textContent || "";
  }

  private getAttachmentsFromMessage(message: any): any[] {
    const attachments: any[] = [];

    if (message?.citations) {
      for (const citation of message.citations) {
        if (citation?.references) {
          for (const reference of citation.references) {
            attachments.push({
              name: reference.name,
              type: reference.extension,
              url: reference.location,
            });
          }
        }
      }
    }

    return attachments;
  }

  showFeedback() {
    this.widget()?.nativeElement?.triggerFeedback();
  }
}
